// src/App.jsx
import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider, useAuth } from "./AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import SchoolLogin from "./SchoolLogin";

import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { TeacherAccessProvider } from "./contexts/TeacherAccessContext";
import { RoleMenusProvider } from "./contexts/RoleMenusContext";

/* ================== Lazy Dashboards ================== */
const TeacherDashboard = lazy(() => import("./dashboards/TeacherDashboard"));
const AdminDashboard = lazy(() => import("./dashboards/AdminDashboard"));
const AccountantDashboard = lazy(() => import("./dashboards/AccountantDashboard"));
const HeadTeacherDashboard = lazy(() => import("./dashboards/HeadTeacherDashboard"));

/* ================== Lazy Pages ================== */
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));

const CommunicationPage = lazy(() => import("./pages/CommunicationPage"));
const ManageEventsPage = lazy(() => import("./pages/ManageEventsPage"));

const ManageStaffPage = lazy(() => import("./pages/ManageStaffPage"));
const ManageStudentsPage = lazy(() => import("./pages/ManageStudentsPage"));

const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const ManageAttendancePage = lazy(() => import("./pages/ManageAttendancePage"));
const AttendanceReportPage = lazy(() => import("./pages/AttendanceReportPage"));

/* Fees */
const ManageFeesPage = lazy(() => import("./pages/ManageFeesPage"));
const FeesReportPage = lazy(() => import("./pages/FeesReportPage"));
const PrintBillPage = lazy(() => import("./pages/PrintBillPage"));
const ManageDiscountsPage = lazy(() => import("./pages/ManageDiscountsPage"));

/* Academics */
const ManageClassTeacherPage = lazy(() => import("./pages/ManageClassTeacherPage"));
const ManageSubjectsPage = lazy(() => import("./pages/ManageSubjectsPage"));
const ManageClassesPage = lazy(() => import("./pages/ManageClassesPage"));
const AssignSubjectsPage = lazy(() => import("./pages/AssignSubjectsPage"));
const ManageAcademicYearsPage = lazy(() => import("./pages/ManageAcademicYearsPage"));
const ManageTermsPage = lazy(() => import("./pages/ManageTermsPage"));

/* Exams */
const PrintExamReportPage = lazy(() => import("./pages/PrintExamReportPage"));
const ManageExamReportPage = lazy(() => import("./pages/ManageExamReportPage"));
const ExamScaleSetupPage = lazy(() => import("./pages/ExamScaleSetupPage"));
const EnterScoresPage = lazy(() => import("./pages/EnterScoresPage"));

/* ✅ NEW PAGES */
const ManageExpensePage = lazy(() => import("./pages/ManageExpensePage"));
const ManageHealthPage = lazy(() => import("./pages/ManageHealthPage"));
const ManageMealPlanPage = lazy(() => import("./pages/ManageMealPlanPage"));
const ManageTimeTablePage = lazy(() => import("./pages/ManageTimeTablePage"));
const PromoteRepeatPage = lazy(() => import("./pages/PromoteRepeatPage"));
const ManageYearGroupPage = lazy(() => import("./pages/ManageYearGroupPage"));
const ManageDivisionsPage = lazy(() => import("./pages/ManageDivisionsPage"));

/* ✅ Alumni */
const AlumniSearchPage = lazy(() => import("./pages/AlumniSearchPage"));

/* ✅ Admin menu console */
const ManageMenusPage = lazy(() => import("./pages/ManageMenusPage"));

/* ================== UI ================== */
const DashboardLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
    <div className="text-center">
      <LoadingSpinner size="large" showLogo={true} />
      <p className="mt-4 text-gray-600 dark:text-gray-300 font-medium">
        Loading your dashboard...
      </p>
    </div>
  </div>
);

/* ================== Role helpers ================== */
const normalizeRole = (role) => {
  if (!role) return "";
  const r = String(role).toLowerCase();
  if (r === "ht" || r === "headteacher") return "headteacher";
  if (r === "ad" || r === "admin") return "admin";
  if (r === "tr" || r === "teacher" || r === "te") return "teacher";
  if (r === "ac" || r === "accountant") return "accountant";
  if (r === "ow" || r === "owner") return "owner";
  return r;
};

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("smh_user_v2");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.userType) return parsed;
  } catch {}
  return null;
};

/* ================== Protected Route ================== */
const ProtectedRoute = ({ children }) => {
  const { user, isLoading, isInitializing } = useAuth();
  const location = useLocation();

  const effectiveUser = user?.userType ? user : getStoredUser();
  const authenticated = !!effectiveUser?.userType;

  if (isInitializing || isLoading) return <DashboardLoading />;
  if (!authenticated) return <Navigate to="/login" replace state={{ from: location }} />;

  return children;
};

/* ================== Role-based Route ================== */
/* ✅ NO RESTRICTIONS: Always allow access */
const RoleRoute = ({ children }) => {
  return children;
};

/* ================== Feature-based Route ================== */
/* ✅ NO RESTRICTIONS: Always allow access */
const FeatureRoute = ({ children }) => {
  return children;
};

/* ================== Dashboard Router ================== */
const DashboardRouter = () => {
  const { user, isLoading, isInitializing } = useAuth();
  const effectiveUser = user?.userType ? user : getStoredUser();

  if (isInitializing || isLoading) return <DashboardLoading />;
  if (!effectiveUser) return <Navigate to="/login" replace />;

  switch (normalizeRole(effectiveUser.userType)) {
    case "teacher":
      return (
        <Suspense fallback={<DashboardLoading />}>
          <TeacherDashboard />
        </Suspense>
      );
    case "admin":
      return (
        <Suspense fallback={<DashboardLoading />}>
          <AdminDashboard />
        </Suspense>
      );
    case "accountant":
      return (
        <Suspense fallback={<DashboardLoading />}>
          <AccountantDashboard />
        </Suspense>
      );
    case "headteacher":
      return (
        <Suspense fallback={<DashboardLoading />}>
          <HeadTeacherDashboard />
        </Suspense>
      );
    default:
      return <Navigate to="/login" replace />;
  }
};

/* ================== Login Route ================== */
const LoginRoute = () => {
  const { isAuthenticated, isLoading, isInitializing, user } = useAuth();
  const effectiveUser = user?.userType ? user : getStoredUser();
  const effectiveAuth = !!effectiveUser?.userType;

  if (!isLoading && !isInitializing && (isAuthenticated || effectiveAuth)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SchoolLogin />;
};

/* ================== Routes ================== */
const AppRoutes = () => {
  const { isLoading, isInitializing, user } = useAuth();
  const effectiveUser = user?.userType ? user : getStoredUser();
  const authenticated = !!effectiveUser?.userType;

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />

      {/* Manage Menus (now NOT restricted by RoleRoute because RoleRoute is no-op) */}
      <Route
        path="/dashboard/manage-menus"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageMenusPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Communications */}
      <Route
        path="/dashboard/communication"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <CommunicationPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Events */}
      <Route
        path="/dashboard/manage-events"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageEventsPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Staff */}
      <Route
        path="/dashboard/manage-staff"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageStaffPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Students */}
      <Route
        path="/dashboard/manage-students"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <FeatureRoute>
                <Suspense fallback={<DashboardLoading />}>
                  <ManageStudentsPage />
                </Suspense>
              </FeatureRoute>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Alumni */}
      <Route
        path="/dashboard/alumni"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <AlumniSearchPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Attendance */}
      <Route
        path="/dashboard/attendance"
        element={
          <ProtectedRoute>
            <Suspense fallback={<DashboardLoading />}>
              <AttendancePage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-attendance"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <FeatureRoute>
                <Suspense fallback={<DashboardLoading />}>
                  <ManageAttendancePage />
                </Suspense>
              </FeatureRoute>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/attendance-report"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <AttendanceReportPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Fees */}
      <Route
        path="/dashboard/manage-fees"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageFeesPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/fees-report"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <FeesReportPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/print-bill"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <PrintBillPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-discount"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageDiscountsPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-expenses"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageExpensePage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Welfare */}
      <Route
        path="/dashboard/manage-health"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageHealthPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-meal-plan"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageMealPlanPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Academics */}
      <Route
        path="/dashboard/class-teacher"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageClassTeacherPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-subjects"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageSubjectsPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/assign-subjects"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <AssignSubjectsPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/classes"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageClassesPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/academic-years"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageAcademicYearsPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/academic-terms"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageTermsPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-timetable"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageTimeTablePage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/promote-repeat"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <PromoteRepeatPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-year-group"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageYearGroupPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-divisions"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ManageDivisionsPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Exams */}
      <Route
        path="/dashboard/print-exam-report"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <PrintExamReportPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/manage-exam"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <FeatureRoute>
                <Suspense fallback={<DashboardLoading />}>
                  <ManageExamReportPage />
                </Suspense>
              </FeatureRoute>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Enter Scores: NO RESTRICTION (only login required) */}
      <Route
        path="/dashboard/exams/enter-scores"
        element={
          <ProtectedRoute>
            <Suspense fallback={<DashboardLoading />}>
              <EnterScoresPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/exams/scale"
        element={
          <ProtectedRoute>
            <RoleRoute>
              <Suspense fallback={<DashboardLoading />}>
                <ExamScaleSetupPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Profile & Settings */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Suspense fallback={<DashboardLoading />}>
              <Profile />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Suspense fallback={<DashboardLoading />}>
              <Settings />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Default */}
      <Route
        path="/"
        element={
          isInitializing ? (
            <DashboardLoading />
          ) : authenticated && !isLoading ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* 404 */}
      <Route
        path="*"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <NotFound />
          </Suspense>
        }
      />
    </Routes>
  );
};

/* ================== Theme Wrapper ================== */
const ThemeWrapper = ({ children }) => {
  const { theme } = useTheme();
  return (
    <div className={theme === "dark" ? "dark" : ""}>
      <div className="App min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        {children}
      </div>
    </div>
  );
};

/* ================== DataProvider fallback ================== */
let DataProvider;
try {
  const DataContextModule = require("./contexts/DataContext");
  DataProvider = DataContextModule.DataProvider;
} catch {
  DataProvider = ({ children }) => children;
}

/* ================== App Root ================== */
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <TeacherAccessProvider>
            <RoleMenusProvider>
              <Router>
                <DataProvider>
                  <ThemeWrapper>
                    <AppRoutes />
                  </ThemeWrapper>
                </DataProvider>
              </Router>
            </RoleMenusProvider>
          </TeacherAccessProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
